import { Alert, Modal, Select, Space, Typography } from "antd";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";

interface Props {
  /** Display name of the profile being linked; null closes the modal. */
  profileName: string | null;
  onClose(): void;
  /** Sends the link request; throws an ApiError on failure. */
  submit(userId: string): Promise<void>;
}

/** Picks an existing login (needs identity.manage to list users) and links it to a teacher or staff profile. */
export function LinkUserModal({ profileName, onClose, submit }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const open = profileName !== null;

  const users = useQuery({
    queryKey: ["users", { search, page: 1, pageSize: 50 }],
    queryFn: async () => {
      const { data, error } = await api.GET("/users", { params: { query: { ...(search ? { Search: search } : {}), Page: 1, PageSize: 50 } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const mutation = useMutation({
    mutationFn: (id: string) => submit(id),
    onSuccess: () => {
      setUserId(null);
      setErrors([]);
    },
    onError: (err: unknown) => {
      const apiError = err instanceof ApiError ? err : undefined;
      const fieldErrors = Object.values(apiError?.fieldErrors() ?? {}).flat();
      setErrors(fieldErrors.length > 0 ? fieldErrors : (apiError?.messages(t("app.error")) ?? [t("app.error")]));
    },
  });

  const close = () => {
    setUserId(null);
    setErrors([]);
    onClose();
  };

  return (
    <Modal
      title={t("linkUser.title")}
      open={open}
      onCancel={close}
      onOk={() => userId && mutation.mutate(userId)}
      okText={t("linkUser.link")}
      cancelText={t("linkUser.cancel")}
      okButtonProps={{ disabled: !userId, loading: mutation.isPending }}
      destroyOnHidden
    >
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Typography.Paragraph style={{ marginBottom: 0 }}>{t("linkUser.intro", { name: profileName ?? "" })}</Typography.Paragraph>
        {errors.length > 0 && <Alert type="error" showIcon role="alert" title={errors.map((m) => <div key={m}>{m}</div>)} />}
        {users.isError && <Alert type="error" showIcon title={t("app.error")} />}
        <Select
          showSearch={{ filterOption: false, onSearch: (value) => setSearch(value.trim()) }}
          {...(userId !== null ? { value: userId } : {})}
          onChange={(value: string) => setUserId(value)}
          loading={users.isFetching}
          placeholder={t("linkUser.search")}
          aria-label={t("linkUser.user")}
          style={{ width: "100%" }}
          options={(users.data?.items ?? []).map((u) => ({
            value: u.id,
            label: `${u.displayName} (${u.userName})${u.isActive ? "" : ` — ${t("linkUser.inactive")}`}`,
          }))}
        />
      </Space>
    </Modal>
  );
}
